import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Shield, Layers, FileText, ArrowRight, Building, Users,
  CheckCircle2, ChevronDown, ChevronRight, Zap, Upload, ClipboardCheck,
  Settings, CreditCard, Bell, Lock, BarChart3, GitBranch, AlertTriangle,
  HelpCircle, Target, Award,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

// ─── Collapsible Section Component ────────────────────────────────────────

function Section({ title, icon: Icon, children, defaultOpen = false, color = 'blue' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
  color?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const colors: Record<string, { bg: string; border: string; icon: string; title: string }> = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', title: 'text-blue-900' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', title: 'text-emerald-900' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', title: 'text-indigo-900' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', title: 'text-amber-900' },
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', title: 'text-slate-900' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', title: 'text-purple-900' },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className={`rounded-2xl border ${c.border} overflow-hidden`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-5 py-4 ${c.bg} hover:brightness-95 transition-all text-left`}
      >
        <Icon size={20} className={c.icon} />
        <span className={`text-base font-bold ${c.title} flex-1`}>{title}</span>
        {open ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
      </button>
      {open && <div className="px-5 py-4 bg-white space-y-4">{children}</div>}
    </div>
  );
}

function StepCard({ number, title, description, link, linkText }: {
  number: number; title: string; description: string; link?: string; linkText?: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">{number}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 text-left">{title}</p>
        <p className="text-xs text-slate-600 text-left mt-0.5">{description}</p>
        {link && (
          <button
            onClick={() => navigate(link)}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mt-2"
          >
            {linkText || 'Go to page'} <ArrowRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function InfoBox({ children, type = 'info' }: { children: React.ReactNode; type?: 'info' | 'warning' | 'success' }) {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  const icons = { info: HelpCircle, warning: AlertTriangle, success: CheckCircle2 };
  const IconComp = icons[type];

  return (
    <div className={`flex items-start gap-2 p-3 rounded-xl border ${styles[type]} text-xs`}>
      <IconComp size={14} className="mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

const GettingStartedGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="text-blue-600" size={28} />
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Getting Started Guide</h1><PageInfoButton /></div>
          </div>
          <p className="text-slate-500 text-left">
            Everything you need to know to set up and manage your compliance platform.
          </p>
        </div>

        {/* Quick Overview */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-3 text-left">How EIS-Tech Works</h2>
          <p className="text-sm text-slate-600 text-left mb-4 text-left">
            EIS-Tech manages the full compliance lifecycle. As a system admin, you set up frameworks and templates.
            Companies receive assigned frameworks, fill in evidence, and submit for audit review. Here's the complete journey:
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
            {[
              { label: 'Deploy Framework', icon: Shield, color: 'bg-blue-100 text-blue-700' },
              { label: 'Build & Publish', icon: Layers, color: 'bg-indigo-100 text-indigo-700' },
              { label: 'Assign to Company', icon: Building, color: 'bg-emerald-100 text-emerald-700' },
              { label: 'Company Fills Evidence', icon: Upload, color: 'bg-amber-100 text-amber-700' },
              { label: 'Audit & Certify', icon: Award, color: 'bg-rose-100 text-rose-700' },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${step.color}`}>
                  <step.icon size={12} /> {step.label}
                </span>
                {i < arr.length - 1 && <ArrowRight size={14} className="text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-3">

          {/* ═══ SECTION 1: Setting Up Compliance ═══ */}
          <Section title="Setting Up Compliance (Admin Flow)" icon={Settings} defaultOpen={true} color="blue">
            <p className="text-sm text-slate-600 text-left">
              This is the setup you do once per compliance standard. After setup, you can assign it to unlimited companies.
            </p>

            <StepCard
              number={0}
              title="Deploy from Framework Library (Optional)"
              description="Pre-built standards like ISO 27001, SOC 2, GDPR are available for one-click deployment. Deploying creates the framework, version, and template automatically. Skip this step if creating a custom framework."
              link="/compliance/definition/library"
              linkText="Open Framework Library"
            />

            <StepCard
              number={1}
              title="Create & Activate a Framework Version"
              description="A framework represents a compliance standard (e.g., 'ISO 27001'). Each framework has versions (e.g., '2022'). Only one version can be active at a time. Draft = being prepared, Active = current version used for compliance, Archived = replaced by newer version."
              link="/compliance/definition/frameworks"
              linkText="Manage Frameworks"
            />

            <StepCard
              number={2}
              title="Build a Compliance Template"
              description="Templates define what companies need to prove. Structure: Scope (grouping) → Section (category) → Requirement (the actual control). Each requirement needs evidence fields (text questions, file uploads, yes/no, etc.) before you can publish."
              link="/compliance/definition/builder"
              linkText="Open Template Builder"
            />

            <InfoBox type="warning">
              <strong>Template hierarchy is strict:</strong> Scope → Section → Requirement. You cannot put a requirement directly under a scope.
              Requirements that generate compliance pages must have at least one evidence field defined.
            </InfoBox>

            <StepCard
              number={3}
              title="Publish the Template"
              description="Publishing locks the template and makes it available for mapping. Draft templates can be edited freely. Published templates cannot be modified — create a new version instead. Archived templates are no longer in use."
              link="/compliance/definition/templates"
              linkText="Manage Templates"
            />

            <StepCard
              number={4}
              title="Map Template to Framework"
              description="Mapping connects your template requirements to a framework version. This tells the system which compliance pages to generate when the framework is assigned to a company. Select template → check nodes → select framework → click 'Map Nodes'."
              link="/compliance/definition/mapping"
              linkText="Open Framework Mapping"
            />

            <InfoBox type="info">
              <strong>Mapping Types:</strong>
              <strong> Direct</strong> = fully satisfies the requirement.
              <strong> Partial</strong> = partially addresses it.
              <strong> Indirect</strong> = supports it indirectly.
              Most mappings should be "Direct".
            </InfoBox>

            <StepCard
              number={5}
              title="Assign Framework to Company"
              description="This is the final step. When you assign a framework, compliance pages are automatically generated for every mapped requirement. The company admin can then assign pages to their team. Only fully set up frameworks (active version + published template + mappings) appear in the dropdown."
              link="/compliance/definition/assignments"
              linkText="Assign Frameworks"
            />

            <InfoBox type="success">
              <strong>Quick Assign Wizard:</strong> The "Quick Assign" button (purple, on every definition page) walks you through all steps in one modal — select framework, map template, assign to company — without leaving the wizard.
            </InfoBox>
          </Section>

          {/* ═══ SECTION 2: What the Company Sees ═══ */}
          <Section title="What the Company Sees (Company Flow)" icon={Building} color="emerald">
            <p className="text-sm text-slate-600 text-left">
              Once you assign a framework, this is what the company experiences.
            </p>

            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Dashboard</p>
                <p className="text-xs text-slate-600 text-left">
                  Company admins see total compliance pages, status breakdown (not started, in progress, submitted, approved),
                  framework progress percentages, risk stats, and recent activity timeline.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Compliance Pages</p>
                <p className="text-xs text-slate-600 text-left">
                  Company admin assigns specific pages to team members. Each team member sees only their assigned pages.
                  They answer structured questions (yes/no, text, numbers, dropdown) and upload evidence files (PDFs, docs, screenshots).
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Submit for Review</p>
                <p className="text-xs text-slate-600 text-left">
                  After filling in a page, the user submits it. The internal auditor reviews, then either approves or requests changes.
                  If rejected, the user gets a notification with the reason and can revise and resubmit.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Evidence Library</p>
                <p className="text-xs text-slate-600 text-left">
                  All uploaded evidence across all frameworks in one place. Filter by status, framework, date.
                  Export as CSV for external auditors.
                </p>
              </div>
            </div>
          </Section>

          {/* ═══ SECTION 3: The Review Cycle ═══ */}
          <Section title="The Audit & Review Cycle" icon={ClipboardCheck} color="indigo">
            <p className="text-sm text-slate-600 text-left mb-3">
              Compliance pages go through a structured review process:
            </p>

            <div className="space-y-2">
              {[
                { status: 'Not Started', desc: 'Page created but nobody has worked on it yet', color: 'bg-slate-100 text-slate-700' },
                { status: 'In Progress', desc: 'User is filling in responses and uploading evidence', color: 'bg-blue-100 text-blue-700' },
                { status: 'Submitted', desc: 'User has submitted the page for internal review', color: 'bg-amber-100 text-amber-700' },
                { status: 'Approved for Audit', desc: 'Internal auditor has approved — ready for external auditor', color: 'bg-emerald-100 text-emerald-700' },
                { status: 'Certified', desc: 'External auditor has certified this requirement — compliance achieved', color: 'bg-green-100 text-green-800' },
              ].map(s => (
                <div key={s.status} className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${s.color} whitespace-nowrap`}>{s.status}</span>
                  <span className="text-xs text-slate-600 text-left">{s.desc}</span>
                </div>
              ))}
            </div>

            <InfoBox type="info">
              <strong>Evidence review:</strong> Individual evidence files are reviewed separately.
              Approved = "INTERNAL_VERIFIED". Rejected = "REJECTED" (a corrective action is auto-created).
              The compliance user and company admin both get notifications on approvals and rejections.
            </InfoBox>

            <InfoBox type="success">
              <strong>Auto-completion:</strong> When ALL compliance pages for a framework are approved or certified,
              the assignment status automatically changes to "Completed".
            </InfoBox>
          </Section>

          {/* ═══ SECTION 4: User Roles ═══ */}
          <Section title="User Roles & Permissions" icon={Users} color="purple">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 font-bold text-slate-500">Role</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">What They Do</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['System Admin', 'Full access — manages all companies, frameworks, templates, system settings, users'],
                    ['Partner Admin', 'Manages their own client companies and frameworks (multi-tenant isolated)'],
                    ['Company Admin', 'Manages their company — assigns pages to team, views dashboard, manages users'],
                    ['Compliance User', 'Fills in assigned compliance pages, uploads evidence, submits for review'],
                    ['Internal Auditor', 'Reviews submitted pages and evidence — approves or requests changes'],
                    ['External Auditor', 'Final certification review — certifies pages for official compliance'],
                  ].map(([role, desc]) => (
                    <tr key={role}>
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{role}</td>
                      <td className="px-3 py-2 text-slate-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InfoBox type="info">
              When assigning compliance pages to team members, only <strong>Compliance Users</strong> appear in the dropdown.
              Company Admins and Auditors are excluded because page completion is not their role.
            </InfoBox>
          </Section>

          {/* ═══ SECTION 5: Supporting Features ═══ */}
          <Section title="Supporting Features" icon={BarChart3} color="amber">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { icon: Target, name: 'Risk Register', desc: 'Track risks by severity with likelihood x impact scoring' },
                { icon: ClipboardCheck, name: 'Control Testing', desc: 'Schedule and record control tests with results' },
                { icon: AlertTriangle, name: 'CAPA', desc: 'Corrective actions — auto-created on evidence rejection' },
                { icon: BookOpen, name: 'Training', desc: 'Create modules with quizzes, track completion' },
                { icon: Building, name: 'Vendor Risk', desc: 'Assess third-party vendors, track contracts' },
                { icon: FileText, name: 'PDF Reports', desc: 'Generate board-ready management reports' },
                { icon: Bell, name: 'Email Notifications', desc: 'Auto-emails on submit, approve, reject, deadlines' },
                { icon: Lock, name: 'Audit Trail', desc: 'Immutable log of every action with hash verification' },
                { icon: Shield, name: 'Evidence Expiry', desc: 'Track when evidence needs refreshing' },
                { icon: Layers, name: 'Cross-Framework', desc: 'See which controls satisfy multiple standards' },
              ].map(f => (
                <div key={f.name} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                  <f.icon size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-800 text-left">{f.name}</p>
                    <p className="text-[10px] text-slate-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ═══ SECTION 6: Platform Settings ═══ */}
          <Section title="Platform Settings" icon={Settings} color="slate">
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Self-Signup Toggle</p>
                <p className="text-xs text-slate-600 text-left">
                  By default, only admins can create company accounts. Enable "Self Signup Enabled" in
                  System Settings → Platform tab to let companies register themselves at /register.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Billing (Stripe)</p>
                <p className="text-xs text-slate-600 text-left">
                  Add Stripe API keys in your server environment variables to enable billing.
                  Supports plans, checkout, customer portal, and webhooks.
                  Two-tier: you bill partners, partners bill their companies via Stripe Connect.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-semibold text-slate-800 text-left">Email Configuration</p>
                <p className="text-xs text-slate-600 text-left">
                  Configure SMTP settings in System Settings → Email/SMTP tab.
                  14 email triggers are pre-configured for compliance events (evidence submitted, approved, rejected, etc.).
                </p>
              </div>
            </div>

            <InfoBox type="warning">
              <strong>First time setup:</strong> Configure email settings before assigning frameworks to companies.
              Without email, users won't receive notifications about their compliance tasks.
            </InfoBox>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default GettingStartedGuide;
